import React, { useState, useEffect } from 'react';
import { ReactStripeElements } from 'react-stripe-elements';
import { useBreakpoints } from 'kb-components/core/useBreakpoints';

import { ReactComponent as CloseIcon } from '../../../../assets/icons/close-icon.svg';
import { OutlineButton } from '../../../../components/Buttons';
import Modal from '../../../../components/Modal';
import Checkbox from '../../../../components/Checkbox';
import PaymentDetails from '../InvoicePayModal/PaymentDetails';
import TextInput from '../../../../components/TextInput/NewTextInput';
import SelectInput from '../../../../components/SelectInput';
import { SelectReasonFlexContainer } from './AzaleaPayModal.styled';
import * as Styled from '../InvoicePayModal/InvoicePayModal.styled';
import useCreatePayment from '../../hooks/use-create-payment';
import { showErrorToast } from '../../../../utilities/notificationUtils';
import { assureProperCentRounding } from '../../../../utilities';

interface Props {
  open: boolean;
  onClose: () => void;
  stripe: ReactStripeElements.StripeProps | null;
}

const REASON_OTHER = 'other';
const REASON_OPTIONS = [
  { label: 'Copay, Coinsurance, Deductible', value: 'co-pay, ded, co-ins' },
  { label: 'Cycle Prepay/Deposit', value: 'deposit' },
  { label: 'Past Due Balance', value: 'past due balance' },
  { label: 'Other', value: REASON_OTHER }
];

interface FormDataValues {
  amount: string;
  reason?: typeof REASON_OPTIONS[number] | undefined;
  reasonOther: string;
  confirmed: boolean;
}

const INITIAL_FORM_DATA: FormDataValues = {
  amount: '',
  reason: undefined,
  reasonOther: '',
  confirmed: false
};

const AzaleaPayModal = ({ stripe, open, onClose }: Props) => {
  const [cardHolderName, setCardHolderName] = useState('');
  const [loading, setloading] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const { isMobile } = useBreakpoints();
  const { createPayment } = useCreatePayment();

  useEffect(() => {
    setFormData(INITIAL_FORM_DATA);
  }, [open]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formData.amount == undefined) {
      showErrorToast('Amount is not a number');
      return;
    }
    setloading(true);
    const amountCents = parseFloat(formData.amount) * 100;
    const reason = formData.reason?.value;
    const reasonDetails = formData.reasonOther;
    await createPayment({ cardHolderName, amountCents, reason, reasonDetails }, stripe, onClose);
    setloading(false);
    setCardHolderName('');
  };

  const updateFormData = (field: string, event: any) => {
    let value;

    if (field == 'reason') {
      value = event;
    } else {
      value = event.currentTarget.value;
    }

    if (field == 'amount') {
      if (Number.isNaN(parseFloat(value))) {
        value = value;
      } else {
        if (value < 0) {
          value = undefined;
        } else {
          value = assureProperCentRounding(value);
        }
      }
    }

    setFormData({ ...formData, [field]: value });
  };

  const toggleConfirmCheckbox = () => {
    setFormData({ ...formData, ['confirmed']: !formData.confirmed });
  };

  const submitButtonIsDisabled =
    loading ||
    !formData.confirmed ||
    !formData.amount ||
    !formData.reason ||
    (formData.reason.value == REASON_OTHER && !formData.reasonOther);

  return (
    <Modal label="Make a payment" open={open} onRequestClose={onClose} mobileFullscreen>
      <Styled.Form onSubmit={onSubmit}>
        {isMobile ? (
          <Styled.CloseArrowButton type="button" onClick={onClose}>
            Your invoices
          </Styled.CloseArrowButton>
        ) : (
          <Styled.CloseIconButton type="button" onClick={onClose}>
            <CloseIcon width={20} height={20} />
          </Styled.CloseIconButton>
        )}

        <Styled.Header>Make a payment</Styled.Header>

        <Styled.FlexContainer>
          <TextInput
            name={'amount'}
            label={'AMOUNT ($)'}
            value={formData.amount}
            spellCheck="false"
            placeholder={'125.60'}
            type="number"
            max={10000}
            step={0.01}
            onChange={event => updateFormData('amount', event)}
          />
        </Styled.FlexContainer>
        <SelectReasonFlexContainer>
          <SelectInput
            label={'REASON'}
            extractTitle={item => (item ? item.label : '')}
            placeholder={'Select'}
            selectedOption={formData.reason}
            onSelect={event => updateFormData('reason', event)}
            options={REASON_OPTIONS}
          />
        </SelectReasonFlexContainer>
        {formData.reason?.value == REASON_OTHER && (
          <Styled.FlexContainer>
            <TextInput
              name={'reasonOther'}
              label={'OTHER REASON'}
              value={formData.reasonOther}
              spellCheck="false"
              placeholder={'Please fill in the reason'}
              type="string"
              onChange={event => updateFormData('reasonOther', event)}
            />
          </Styled.FlexContainer>
        )}
        <PaymentDetails
          cardHolderName={cardHolderName}
          onCardHolderNameChange={setCardHolderName}
        />
        <Styled.FlexContainer>
          <Checkbox
            label={'I understand that any payment may be applied to a past due balance.'}
            isChecked={formData.confirmed}
            onChange={() => toggleConfirmCheckbox()}
            name="confirmed"
            error={false}
          />
        </Styled.FlexContainer>
        <Styled.FormActions>
          <Styled.PayButton type="submit" disabled={submitButtonIsDisabled} text="PAY" />
          <OutlineButton type="button" onClick={onClose}>
            Cancel
          </OutlineButton>
        </Styled.FormActions>
      </Styled.Form>
    </Modal>
  );
};

export default AzaleaPayModal;
